import { useState, type FormEvent } from "react";

const FORM_ID = "guatian-lead-form";
const SUBMIT_ADDR = "https://readdy.ai/api/form/dahbg3bp14a1h3mr6hqg";

type Status = "idle" | "submitting" | "success" | "error";

export default function LeadForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");
  const [message, setMessage] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const payload = new FormData(form);

    const honeypot = String(payload.get("company_alt") || "").trim();
    if (honeypot !== "") {
      setStatus("success");
      setFormError("");
      form.reset();
      setMessage("");
      return;
    }

    setStatus("submitting");
    setFormError("");

    const body = new URLSearchParams();
    payload.forEach((value, key) => {
      const text = String(value);
      if (key === "company_alt") return;
      if (text.trim() === "") return;
      body.append(key, text);
    });

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      const responseText = await response.text();

      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = serverMsg.toLowerCase().includes("spam") || serverMsg.includes("垃圾");

      const ok = response.ok && parsed?.code === "OK" && !isSpam;

      if (ok) {
        setStatus("success");
        form.reset();
        setMessage("");
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后重试或直接通过邮箱联系我们。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交没有成功，请检查网络后重试。");
    }
  };

  return (
    <form
      id={FORM_ID}
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg bg-background-50 border border-background-200 p-6 md:p-8"
    >
      <h3 className="font-heading font-bold text-lg text-foreground-950">线索征集与商务合作</h3>
      <p className="mt-2 text-sm text-foreground-600">
        发现盘点有疏漏、想提供话题后续进展，或有商务合作需求，都可以在这里告诉我们。我们会按工作日处理，并在核实后回复。
      </p>

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="gt-name" className="block text-sm text-foreground-700 mb-1.5">
            称呼
          </label>
          <input
            id="gt-name"
            name="name"
            type="text"
            required
            placeholder="你的昵称或称呼"
            className="w-full px-4 py-2.5 rounded-md bg-background-100 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          />
        </div>
        <div>
          <label htmlFor="gt-email" className="block text-sm text-foreground-700 mb-1.5">
            邮箱
          </label>
          <input
            id="gt-email"
            name="email"
            type="email"
            required
            placeholder="用于接收回复的邮箱"
            className="w-full px-4 py-2.5 rounded-md bg-background-100 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          />
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="gt-topic" className="block text-sm text-foreground-700 mb-1.5">
            相关话题（选填）
          </label>
          <input
            id="gt-topic"
            name="topic"
            type="text"
            placeholder="例如：某期年度盘点的后续进展"
            className="w-full px-4 py-2.5 rounded-md bg-background-100 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          />
        </div>
        <div>
          <label htmlFor="gt-type" className="block text-sm text-foreground-700 mb-1.5">
            类型
          </label>
          <select
            id="gt-type"
            name="type"
            className="w-full px-4 py-2.5 rounded-md bg-background-100 border border-background-300 text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
          >
            <option value="线索提供">线索提供</option>
            <option value="内容更正">内容更正</option>
            <option value="商务合作">商务合作</option>
            <option value="读者反馈">读者反馈</option>
          </select>
        </div>
      </div>

      <div className="mt-4">
        <label htmlFor="gt-message" className="block text-sm text-foreground-700 mb-1.5">
          内容说明
        </label>
        <textarea
          id="gt-message"
          name="message"
          required
          maxLength={500}
          rows={5}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="请尽量说明信息来源与时间，便于我们核实（最多 500 字）"
          className="w-full px-4 py-2.5 rounded-md bg-background-100 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 resize-none"
        />
        <p className="mt-1.5 text-xs text-foreground-400 text-right">{message.length} / 500</p>
      </div>

      <div className="mt-4">
        <label htmlFor="gt-contact" className="gt-field-optional" aria-hidden="true">
          请勿填写此字段
        </label>
        <input
          id="gt-contact"
          name="company_alt"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
          className="gt-field-optional"
        />
      </div>

      {status === "error" && formError && (
        <p className="mt-4 text-sm text-primary-700 bg-primary-50 border border-primary-200 rounded-md px-4 py-3">
          <i className="ri-error-warning-line mr-1.5" />
          {formError}
        </p>
      )}

      {status === "success" && (
        <p className="mt-4 text-sm text-secondary-800 bg-secondary-50 border border-secondary-200 rounded-md px-4 py-3">
          <i className="ri-checkbox-circle-line mr-1.5" />
          已收到你的信息，我们会按工作日处理并尽快回复，感谢你帮助瓜田把内容做得更准确。
        </p>
      )}

      <button
        type="submit"
        disabled={status === "submitting"}
        className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
      >
        <i className={status === "submitting" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"} />
        {status === "submitting" ? "提交中..." : "提交信息"}
      </button>
    </form>
  );
}